import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  Patient,
  ITypesensePatientWithRef,
} from '@principle-theorem/principle-core';
import { DAY_MONTH_YEAR_FORMAT, toMoment } from '@principle-theorem/shared';
import { compact } from 'lodash';
import { ReplaySubject, combineLatest, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { PatientStatus } from '@principle-theorem/principle-core/interfaces';
import { PatientStatusChipComponent } from '../patient-status-chip/patient-status-chip.component';

const detailsSeparator = ' · ';

@Component({
  selector: 'pr-patient-selector-item',
  templateUrl: './patient-selector-item.component.html',
  styleUrls: ['./patient-selector-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, PatientStatusChipComponent],
})
export class PatientSelectorItemComponent {
  readonly patientStatus = PatientStatus;
  patient$ = new ReplaySubject<ITypesensePatientWithRef>(1);
  details$: Observable<string>;

  @Input()
  set patient(patient: ITypesensePatientWithRef) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  constructor() {
    const phone$ = this.patient$.pipe(
      map((patient) => ({
        ...patient,
        contactNumbers: patient.contactNumbers.map((contactNumber) => ({
          label: '',
          number: contactNumber,
        })),
      })),
      switchMap(Patient.resolveFirstContactNumber)
    );
    this.details$ = combineLatest([this.patient$, phone$]).pipe(
      map(([patient, phone]) =>
        compact([
          PatientSelectorItemComponent._dateOfBirthWithAge(patient),
          phone,
          patient.email,
        ]).join(detailsSeparator)
      )
    );
  }

  private static _dateOfBirthWithAge(
    patient: ITypesensePatientWithRef
  ): string | undefined {
    if (!patient.dateOfBirth) {
      return;
    }
    const dateOfBirth = toMoment(patient.dateOfBirth).format(
      DAY_MONTH_YEAR_FORMAT
    );
    const age = Patient.displayAge(patient);
    return age ? `${dateOfBirth} (${age})` : dateOfBirth;
  }
}
